import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { updateFieldOptions } from "../../services/consultationform";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";

import { FormField } from "../../types/forms";

interface MedicalSectionProps {
  userId: string;
  templateId: string;
  isEditable?: boolean;
  readOnly?: boolean;
  fields?: FormField[];
  value?: Record<string, boolean>;
  onChange?: (value: Record<string, boolean>) => void;
}

interface MedicalField {
  id: string;
  label: string;
  checked: boolean;
}

export default function MedicalSection({
  userId,
  templateId,
  isEditable = false,
  readOnly = false,
  fields,
  value,
  onChange,
}: MedicalSectionProps) {
  const storageKey = `${userId}_${templateId}_medicalfields`;

  const [storageFields, setFields] = useLocalStorage<MedicalField[]>(storageKey, [
    { id: crypto.randomUUID(), label: "Heart Disease", checked: false },
    { id: crypto.randomUUID(), label: "Diabetes", checked: false },
    { id: crypto.randomUUID(), label: "Asthma", checked: false },
    { id: crypto.randomUUID(), label: "Allergies", checked: false },
    { id: crypto.randomUUID(), label: "High Blood Pressure", checked: false },
  ]);

  const [availableFields] = useState<string[]>([
    "Anemia",
    "Arthritis",
    "Back Pain",
    "Cancer",
    "Chronic Bronchitis",
    "COPD (Chronic Obstructive Pulmonary Disease)",
    "Depression",
    "Epilepsy or Seizures",
    "Glaucoma",
    "Hepatitis A/B/C",
    "HIV/AIDS",
    "Hypothyroidism / Hyperthyroidism",
    "Kidney Disease",
    "Liver Disease",
    "Migraine",
    "Obesity",
    "Osteoporosis",
    "Pregnancy (currently or recently)",
    "Skin Disorders (eczema, psoriasis, etc.)",
    "Tuberculosis (TB)",
    "Ulcers or Digestive Disorders",
    "Varicose Veins",
    "Vertigo or Dizziness",
    "Any other chronic illness",
  ]);

  const [customInput, setCustomInput] = useState("");
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" | "" }>({
    text: "",
    type: "",
  });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (message.text) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => setMessage({ text: "", type: "" }), 300);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleToggleCheck = (id: string) => {
    if (!isEditable) {
      setFields(storageFields.map((f) => (f.id === id ? { ...f, checked: !f.checked } : f)));
    }
  };

  const handleAddField = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) {
      setMessage({ text: "Please enter a valid medical condition.", type: "error" });
      return;
    }
    if (storageFields.some((f) => f.label === trimmed)) {
      setMessage({ text: "This condition is already added.", type: "error" });
      return;
    }
    setFields([...storageFields, { id: crypto.randomUUID(), label: trimmed, checked: false }]);
    setCustomInput("");
  };

  const handleDeleteField = (id: string) => {
    setFields(storageFields.filter((f) => f.id !== id));
  };

  // Handle API fields (checkbox_group) vs Local Storage
  const [localOptions, setLocalOptions] = useState<string[]>([]);
  const [apiFieldId, setApiFieldId] = useState<string | number | null>(null);

  useEffect(() => {
    // Check if we have a checkbox_group field from API
    const groupField = fields?.find(f => f.type === "checkbox_group");
    if (groupField && groupField.options && Array.isArray(groupField.options)) {
      setLocalOptions(groupField.options);
      setApiFieldId(groupField.id);
    } else {
      // Fallback to local storage mapping if no API data matches
      setLocalOptions(storageFields.map(f => f.label));
    }
  }, [fields, storageFields]);

  // Combined render list
  const displayItems = localOptions.map((opt, idx) => ({
    id: apiFieldId ? `opt-${idx}` : (storageFields[idx]?.id || `local-${idx}`),
    label: opt,
    checked: false // We don't track checked state for the template builder, only for the filled form
  }));

  const handleAddOption = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) {
      setMessage({ text: "Please enter a valid condition.", type: "error" });
      return;
    }
    if (localOptions.includes(trimmed)) {
      setMessage({ text: "Condition already exists.", type: "error" });
      return;
    }

    // Update local state
    const newOptions = [...localOptions, trimmed];
    setLocalOptions(newOptions);
    setCustomInput("");

    // If API connected, save to backend
    if (apiFieldId) {
      updateFieldOptions(apiFieldId, newOptions).catch(err => {
        console.error("Failed to update options:", err);
        setMessage({ text: "Failed to save condition.", type: "error" });
        // Optional: Revert local state on failure
      });
    } else {
      // Update local storage if in legacy mode
      setFields([...storageFields, { id: crypto.randomUUID(), label: trimmed, checked: false }]);
    }
  };

  const handleRemoveOption = (labelToRemove: string) => {
    const newOptions = localOptions.filter(opt => opt !== labelToRemove);
    setLocalOptions(newOptions);

    if (apiFieldId) {
      updateFieldOptions(apiFieldId, newOptions).catch(err => {
        console.error("Failed to remove option:", err);
        setMessage({ text: "Failed to remove condition.", type: "error" });
      });
    } else {
      const fieldIdToRemove = storageFields.find(f => f.label === labelToRemove)?.id;
      if (fieldIdToRemove) setFields(storageFields.filter(f => f.id !== fieldIdToRemove));
    }
  };

  // Calculate actual value (handle both raw and wrapped with field ID)
  const getActualValue = (): Record<string, boolean> => {
    if (apiFieldId && value && typeof value === 'object' && value !== null && (value as any)[apiFieldId]) {
      return (value as any)[apiFieldId] as Record<string, boolean>;
    }
    return (value as Record<string, boolean>) || {};
  };

  const actualValue = getActualValue();

  const handleToggleCheckInternal = (label: string) => {
    if (readOnly) return;
    if (onChange) {
      const newInnerValue = { ...actualValue, [label]: !actualValue[label] };
      if (apiFieldId) {
        onChange({ [apiFieldId]: newInnerValue } as any);
      } else {
        onChange(newInnerValue);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1.5">
        <h2 className="text-[15px] font-bold text-gray-800 tracking-tight">Medical Information</h2>
        <p className="text-[13px] text-gray-400 font-medium leading-relaxed">Please check all that apply to you.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        {displayItems.length > 0 ? (
          displayItems.map((item) => (
            <div key={item.id} className="flex items-start gap-3 p-2 rounded-xl transition-colors hover:bg-gray-50/50 group">
              {!isEditable && (
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    checked={!!actualValue[item.label]}
                    onChange={() => handleToggleCheckInternal(item.label)}
                    className="accent-[#885ABB] w-4.5 h-4.5 cursor-pointer rounded border-gray-300 transition-all hover:scale-110 disabled:cursor-not-allowed"
                    disabled={readOnly}
                  />
                </div>
              )}
              <div className="flex-1 text-[14px] text-gray-700 leading-snug font-medium group-hover:text-gray-900 transition-colors">
                {item.label}
              </div>
              {isEditable && (
                <button
                  onClick={() => handleRemoveOption(item.label)}
                  className="text-gray-300 hover:text-red-500 transition-all scale-90 hover:scale-100 mt-0.5"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))
        ) : (
          <p className="col-span-full text-xs text-gray-400 italic text-center py-4 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            No medical conditions added yet.
          </p>
        )}
      </div>
      {isEditable && !readOnly && (
        <div className="border-t border-gray-200 pt-3 space-y-2">
          {message.text && (
            <div
              className={`text-xs font-medium px-2 py-1 rounded-md transition-all duration-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
                } ${message.type === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
                }`}
            >
              {message.text}
            </div>
          )}
          <label className="block text-xs font-medium text-gray-600">
            Add New Medical Condition
          </label>
          <div className="flex items-center gap-2">
            <Select onValueChange={(value) => handleAddOption(value)}>
              <SelectTrigger className="flex-1 h-9 text-sm border-gray-300 bg-gray-50">
                <SelectValue placeholder="Choose condition to add..." />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-60 overflow-y-auto">
                {availableFields.map((label) => (
                  <SelectItem key={label} value={label}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="text"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              placeholder="Or enter custom condition..."
              className="flex-1 h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
            <button
              onClick={() => handleAddOption(customInput)}
              className="p-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white transition-all"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
